import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const HERO_NABAWI = `${API}/api/static/hero/home.jpg`;
const PATTERN = "https://static.prod-images.emergentagent.com/jobs/c612f9b0-bd89-4c85-9451-5b81c8951306/images/165161635b2ebd7638221dd2398e9e8e9b9cc0c1d356138bcff47de1dc799d9f.png";

const STATS = [
  { k: "2016", labelKey: "hero.stat1" },
  { k: "PPIU", l: "Izin No. 91205097027470003" },
  { k: "PIHK", l: "Izin No. 91205097027470005" },
  { k: "A", l: "Akreditasi Kemenag" },
];

export default function HeroSection() {
  const { t } = useLang();
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background image, full-bleed with extended vertical fade */}
      <div className="absolute inset-0 z-0">
        <img src={HERO_NABAWI} alt="Masjid an-Nabawi" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FDFCF8]/95 via-[#FDFCF8]/70 to-[#FDFCF8]/5" />
        {/* Extended vertical soft fade — hero image dissolves into background over the bottom 40% */}
        <div className="absolute inset-0"
             style={{ background: "linear-gradient(to bottom, rgba(253,252,248,0.55) 0%, rgba(253,252,248,0) 30%, rgba(253,252,248,0) 55%, rgba(253,252,248,0.85) 80%, rgba(253,252,248,1) 100%)" }} />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: `url(${PATTERN})`, backgroundSize: "260px" }} />
      </div>
      {/* Bottom over-fade — extra 24rem so section boundary is invisible */}
      <div className="pointer-events-none absolute -bottom-2 inset-x-0 h-96 z-[1]" aria-hidden
           style={{ background: "linear-gradient(to bottom, rgba(253,252,248,0) 0%, rgba(253,252,248,0.4) 30%, rgba(253,252,248,0.85) 60%, #FDFCF8 100%)" }} />

      <div className="container-x py-32 lg:py-40 w-full relative z-10">
        <div className="max-w-3xl anim-fade-up">
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-accent" />
            <p className="overline !text-accent">{t("hero.overline")}</p>
          </div>
          <h1 className="h-display text-primary text-balance">
            {t("hero.titleA")} <span className="italic text-accent">{t("hero.titleB")}</span> {t("hero.titleC")}
          </h1>
          <p className="mt-8 text-lg lg:text-xl text-primary/75 leading-relaxed max-w-2xl">{t("hero.sub")}</p>

          {/* Trust Badge — Google Reviews + Jamaah count */}
          <div className="mt-6 inline-flex items-center gap-4 rounded-full bg-white/70 backdrop-blur-sm border border-accent/30 pl-4 pr-5 py-2 shadow-sm" data-testid="trust-badge">
            <div className="flex items-center gap-1 text-accent" aria-label="4.9 stars">
              {[0,1,2,3,4].map((i) => (
                <svg key={i} className="h-4 w-4 fill-current" viewBox="0 0 20 20"><path d="M10 15.27L16.18 19l-1.64-7.03L20 7.24l-7.19-.61L10 0 7.19 6.63 0 7.24l5.46 4.73L3.82 19z"/></svg>
              ))}
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm">
              <span className="font-serif text-primary font-bold text-base">4.9</span>
              <span className="text-primary/60">·</span>
              <span className="text-primary/80"><strong className="text-primary">1725</strong> Ulasan Google</span>
              <span className="hidden sm:inline text-primary/60">·</span>
              <span className="hidden sm:inline text-primary/80"><strong className="text-primary">15.000+</strong> Jamaah</span>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/packages" className="btn-accent" data-testid="hero-cta-packages">
              {t("hero.cta1")} <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="https://wa.me/6282114294101"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 text-primary bg-white/50 backdrop-blur-sm px-7 py-3 text-sm font-medium tracking-wide transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5"
              data-testid="hero-cta-wa"
            >
              {t("hero.cta2")}
            </a>
          </div>

          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl">
            {STATS.map((s) => (
              <div key={s.k} className="border-l-2 border-accent pl-4">
                <div className="font-serif text-3xl lg:text-4xl text-primary">{s.k}</div>
                <div className="text-xs text-primary/60 mt-1 leading-snug">
                  {s.labelKey ? t(s.labelKey) : s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-primary/50">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-accent to-transparent" />
      </div>
    </section>
  );
}
